import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AuthContext";
import { api } from "@/lib/api";
import MascotLoader from "@/components/MascotLoader";
import FeelingsCheckIn from "@/components/FeelingsCheckIn";
import {
  BookOpen, Sparkles, PenLine, Calculator, FlaskConical, Languages, ArrowRight,
  Zap, Trophy, Target, CheckCircle2, Crown, Lock, Star, ClipboardList,
  MessagesSquare, Globe, Heart, HandHeart,
} from "lucide-react";

const ICONS = {
  "book-open": BookOpen, sparkles: Sparkles, "pen-line": PenLine,
  calculator: Calculator, "flask-conical": FlaskConical, languages: Languages,
  "messages-square": MessagesSquare, globe: Globe, heart: Heart, "hand-heart": HandHeart,
};

export default function SubjectHub() {
  const { user, overlay, refreshCurriculum } = useApp();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState(null);
  const [access, setAccess] = useState(true);
  const [stickers, setStickers] = useState([]);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    refreshCurriculum && refreshCurriculum();
    api.listSubjects().then((d) => { setSubjects(d.subjects); setAccess(d.has_full_access); }).catch(() => setSubjects([]));
    if (user?.id) api.getStickers(user.id).then((d) => setStickers(d.stickers || [])).catch(() => {});
    api.listLessonAssignments().then((d) => setAssignments(d.assignments || [])).catch(() => {});
  }, [refreshCurriculum, user]);

  if (!subjects) return <MascotLoader message="Loading your world…" />;

  const points = overlay?.points ?? 0;
  const earnedStickers = stickers.filter((s) => s.earned);
  // Students never see plan/upgrade UI — subscriptions belong to their parent/teacher.
  const showUpgradeBanner = false;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">ZoodLeap</div>
      <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1F2937]">Hi {user?.name}! 👋</h1>
      <p className="mt-2 font-body text-base sm:text-lg text-[#6B7280]">Pick a subject and start exploring.</p>

      <FeelingsCheckIn onPoints={() => refreshCurriculum && refreshCurriculum()} />

      {showUpgradeBanner && (
        <motion.button
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onClick={() => navigate("/pricing")}
          data-testid="hub-upgrade-banner"
          className="mt-6 flex w-full items-center justify-between gap-3 rounded-3xl bg-gradient-to-r from-[#0D9488] to-[#7A3FF2] p-5 text-left text-white shadow-md transition-transform hover:scale-[1.01]">
          <div className="flex items-center gap-3">
            <Crown size={26} className="text-[#F59E0B]" />
            <div>
              <div className="font-heading text-lg font-extrabold">Unlock the full academy</div>
              <div className="font-body text-sm text-white/85">You're on the free plan — upgrade to open every PreK–8 lesson.</div>
            </div>
          </div>
          <span className="hidden shrink-0 rounded-full bg-white px-4 py-2 font-heading font-bold text-[#0D9488] sm:inline">See plans</span>
        </motion.button>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
          <div className="flex items-center gap-2 text-[#F59E0B]"><Zap size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">Points</span></div>
          <div className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]" data-testid="hub-points">{points}</div>
        </div>
        <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
          <div className="flex items-center gap-2 text-[#7A3FF2]"><Trophy size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">Stickers</span></div>
          <div className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]" data-testid="hub-stickers">{earnedStickers.length}</div>
        </div>
        <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
          <div className="flex items-center gap-2 text-[#10B981]"><CheckCircle2 size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">Subjects</span></div>
          <div className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]">{subjects.length}</div>
        </div>
      </div>

      {/* Prominent Cursive Writing Studio hero — free & unlocked for everyone */}
      <motion.button
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        onClick={() => navigate("/writing")} data-testid="cursive-studio-hero"
        className="group mt-6 flex w-full items-center gap-4 rounded-3xl bg-gradient-to-r from-[#7A3FF2] via-[#0D9488] to-[#10B981] p-5 text-left text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
          <PenLine size={32} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">Free · unlocked</span>
            <span className="font-heading text-2xl font-extrabold">Cursive Writing Studio</span>
          </div>
          <p className="mt-1 font-body text-sm text-white/90">Trace every letter, join words, and earn the Cursive Champion sticker — all levels open.</p>
        </div>
        <ArrowRight size={26} className="shrink-0 transition-transform group-hover:translate-x-1" />
      </motion.button>

      {assignments.length > 0 && (
        <div className="mt-8" data-testid="assigned-section">
          <div className="flex items-center gap-2">
            <ClipboardList size={22} className="text-[#7A3FF2]" />
            <h2 className="font-heading text-2xl font-bold text-[#1F2937]">Assigned to you</h2>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {assignments.map((a, i) => {
              const Icon = ICONS[a.subject?.icon] || Target;
              return (
                <motion.button key={a.id}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  onClick={() => navigate(`/learn/${a.lesson_id}`)} data-testid={`assigned-card-${a.lesson_id}`}
                  className="group flex items-center gap-4 rounded-3xl border-2 p-5 text-left transition-all hover:-translate-y-1 hover:shadow-lg"
                  style={{ borderColor: a.completed ? "#10B981" : a.subject?.color || "#0D9488" }}>
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: a.subject?.color || "#0D9488" }}>
                    <Icon size={26} strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-lg font-bold text-[#1F2937] line-clamp-1">{a.title}</span>
                      {a.completed
                        ? <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[10px] font-bold text-[#047857]"><CheckCircle2 size={11} /> Done</span>
                        : <span className="shrink-0 whitespace-nowrap rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] font-bold text-[#B45309]">To do</span>}
                    </div>
                    <p className="mt-0.5 font-body text-xs text-[#6B7280] line-clamp-1">{a.note || `From ${a.assigned_by_name || "your teacher"} · ${a.subject?.name}`}</p>
                  </div>
                  <ArrowRight size={20} className="shrink-0 transition-transform group-hover:translate-x-1" style={{ color: a.subject?.color || "#0D9488" }} />
                </motion.button>
              );
            })}
          </div>
        </div>
      )}

      <h2 className="mt-10 font-heading text-2xl font-bold text-[#1F2937]">Your Subjects</h2>
      <div className="mt-4 grid gap-5 sm:grid-cols-2">
        {subjects.map((s, i) => {
          const Icon = ICONS[s.icon] || Target;
          const isWriting = !!s.link;
          const hasCursive = !!s.cursive_link;
          const pct = isWriting ? (overlay?.overall_progress_percentage ?? 0) : s.progress_percentage;
          return (
            <motion.button
              key={s.id}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              onClick={() => (isWriting ? navigate(s.link) : navigate(`/subject/${s.id}`))}
              data-testid={`subject-card-${s.id}`}
              className="group flex items-center gap-4 rounded-3xl border border-[#E5DECF] bg-white p-6 text-left transition-all hover:-translate-y-1 hover:shadow-lg">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: s.color }}>
                <Icon size={30} strokeWidth={2.2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-heading text-xl font-bold text-[#1F2937]">{s.name}</span>
                  <span className="rounded-full bg-[#F2ECE1] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">{s.grade}</span>
                  {!access && !isWriting && s.lessons_total > (s.free_lessons || 0) && <Lock size={14} className="text-[#94A3B8]" />}
                </div>
                <p className="mt-0.5 font-body text-sm text-[#6B7280] line-clamp-1">{s.tagline}</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="h-2.5 w-32 max-w-[40vw] overflow-hidden rounded-full bg-[#F2ECE1]">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: s.color }} />
                  </div>
                  <span className="text-xs font-bold text-[#6B7280]">
                    {isWriting ? `${pct}%` : `${s.lessons_completed}/${s.lessons_total} lessons`}
                  </span>
                  {hasCursive && <span className="rounded-full bg-[#0D9488]/10 px-2 py-0.5 text-[10px] font-bold text-[#0D9488]" data-testid="cursive-studio-badge">Cursive {overlay?.overall_progress_percentage ?? 0}%</span>}
                </div>
              </div>
              <ArrowRight size={22} className="shrink-0 transition-transform group-hover:translate-x-1" style={{ color: s.color }} />
            </motion.button>
          );
        })}
      </div>

      {/* Sticker shelf */}
      <h2 className="mt-10 font-heading text-2xl font-bold text-[#1F2937]">Your Sticker Book</h2>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {stickers.map((st) => (
          <div key={st.id} data-testid={`sticker-${st.id}`}
            className={`flex flex-col items-center rounded-3xl border-2 p-5 text-center transition-all ${st.earned ? "bg-white" : "border-dashed border-[#E5DECF] bg-[#F2ECE1]/40 opacity-70"}`}
            style={st.earned ? { borderColor: st.color } : {}}>
            <div className="flex h-14 w-14 items-center justify-center rounded-full" style={{ background: st.earned ? st.color : "#CBD5E1" }}>
              {st.earned ? <Star size={26} className="text-white" /> : <Lock size={22} className="text-white" />}
            </div>
            <div className="mt-2 font-heading text-sm font-bold text-[#1F2937]">{st.name}</div>
            <div className="font-body text-[11px] text-[#6B7280]">{st.earned ? "Earned!" : "Finish the subject"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
